import React, { createContext, useContext, useEffect, useState } from 'react'
import type { User, Session } from '@supabase/supabase-js'
import { supabase } from '../services/supabase'

interface AuthContextType {
  user: User | null
  session: Session | null
  loading: boolean
  signIn: (email: string, password: string) => Promise<{ error: Error | null }>
  signUp: (email: string, password: string, nome: string) => Promise<{ error: Error | null }>
  signOut: () => Promise<void>
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

export const useAuth = () => {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}

interface AuthProviderProps {
  children: React.ReactNode
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null)
  const [session, setSession] = useState<Session | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    // Obter sessão inicial com timeout
    const getInitialSession = async () => {
      try {
        const timeoutPromise = new Promise((_, reject) => {
          setTimeout(() => reject(new Error('Timeout ao obter sessão inicial')), 10000)
        })

        const sessionPromise = supabase.auth.getSession()

        const { data: { session } } = await Promise.race([sessionPromise, timeoutPromise]) as any

        setSession(session)
        setUser(session?.user ?? null)
        setLoading(false)
      } catch (error) {
        console.error('Erro ao obter sessão inicial:', error)
        // Em caso de erro, assumir que não há sessão ativa
        setSession(null)
        setUser(null)
        setLoading(false)
      }
    }

    getInitialSession()

    // Escutar mudanças de autenticação com tratamento de erro
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event, session) => {
      try {
        console.log('Auth state change:', event, session?.user?.email || 'no user')
        setSession(session)
        setUser(session?.user ?? null)
        setLoading(false)
      } catch (error) {
        console.error('Erro no onAuthStateChange:', error)
        setLoading(false)
      }
    })

    return () => {
      try {
        subscription.unsubscribe()
      } catch (error) {
        console.warn('Erro ao cancelar subscription:', error)
      }
    }
  }, [])

  const signIn = async (email: string, password: string) => {
    try {
      const timeoutPromise = new Promise((_, reject) => {
        setTimeout(() => reject(new Error('Timeout no login')), 15000)
      })

      const loginPromise = supabase.auth.signInWithPassword({
        email,
        password,
      })

      const result = await Promise.race([loginPromise, timeoutPromise])
      return { error: (result as any).error }
    } catch (error) {
      console.error('Erro no signIn:', error)
      return { error: error as Error }
    }
  }

  const signUp = async (email: string, password: string, nome: string) => {
    try {
      const timeoutPromise = new Promise((_, reject) => {
        setTimeout(() => reject(new Error('Timeout no cadastro')), 15000)
      })

      const signUpPromise = supabase.auth.signUp({
        email,
        password,
        options: {
          data: {
            nome,
          },
        },
      })

      const result = await Promise.race([signUpPromise, timeoutPromise])
      return { error: (result as any).error }
    } catch (error) {
      console.error('Erro no signUp:', error)
      return { error: error as Error }
    }
  }

  const signOut = async () => {
    try {
      // Tentar fazer logout no servidor com timeout
      const timeoutPromise = new Promise((_, reject) => {
        setTimeout(() => reject(new Error('Timeout')), 5000)
      })

      const logoutPromise = supabase.auth.signOut()

      await Promise.race([logoutPromise, timeoutPromise])

      console.log('Logout do servidor realizado com sucesso')
    } catch (error) {
      console.warn('Erro no logout do servidor, limpando sessão local:', error)

      // Fallback: limpar estado local mesmo se o servidor falhar
      setSession(null)
      setUser(null)

      // Limpar storage local se existir
      try {
        localStorage.removeItem('supabase.auth.token')
        sessionStorage.removeItem('supabase.auth.token')
      } catch (storageError) {
        console.warn('Erro ao limpar storage:', storageError)
      }
    }
  }

  const value = {
    user,
    session,
    loading,
    signIn,
    signUp,
    signOut,
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}
